import { Space, Typography } from 'antd'
import { observer } from 'mobx-react-lite'

import { OffersCountBadge } from '@/Containers/TransportationSection/Components'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { EntityHeader } from '@/Ship/Components/EntityCard'
import { StatusTag } from '@/Ship/Components/StatusTag'
import { formatMoneyWithCurrency } from '@/Ship/Helpers/formatNumber'

import { APPLICATION_FIELD_LABELS as L } from '../Contracts/ApplicationFieldLabels'

import type { TransportRequestRelationRef, TransportRequestStatusRef } from '@/Containers/TransportationSection/Components'
import type { ApplicationFormContextLike } from '../Contracts/IApplication'
import type { ReactNode } from 'react'

/** Ключі ресурсів карток, які відкриваються другою шухлядою — без імпорту чужих контейнерів. */
const LINKED_RESOURCES = {
    client: 'Crm.Client',
    carrier: 'Crm.Carrier',
    request: 'Transportation.Request',
    tender: 'Transportation.Tender',
} as const

export type ApplicationLinkKind = keyof typeof LINKED_RESOURCES

const uuidOf = (ref: TransportRequestRelationRef | null | undefined): string | null => {
    if (typeof ref?.uuid === 'string' && ref.uuid !== '') return ref.uuid

    const iri = ref?.['@id']

    return typeof iri === 'string' && iri !== '' ? (iri.split('/').filter(Boolean).pop() ?? null) : null
}

/**
 * Статус бейджем кольору довідника і поруч — бейдж «Кількість пропозицій»
 * (`SPEC-021` §8.1; легасі `TransformOffersCountTableColumnValueTask.jsx`).
 * На «Рейсах» бейджа немає (`isShowOffersCount={false}`, `TripPage.jsx`).
 */
export const ApplicationStatus = ({
    status,
    countOffers,
    showOffers = true,
}: {
    status: TransportRequestStatusRef | null | undefined
    countOffers?: unknown
    showOffers?: boolean
}) =>
    status && typeof status.title === 'string' ? (
        <Space size={6}>
            <StatusTag label={status.title} color={status.color} />
            {showOffers ? <OffersCountBadge count={countOffers} /> : null}
        </Space>
    ) : (
        <>—</>
    )

/**
 * Назва пов'язаного запису посиланням на його картку (легасі
 * `ApplicationCrud.jsx:31-60`, `ApplicationFormTemplate.jsx`): клієнт,
 * перевізник, запит, тендер. Картка відкривається другою шухлядою поверх
 * списку чи заявки; клік не доходить до рядка таблиці.
 */
export const ApplicationLink = ({
    kind,
    record,
    fallbackTitle,
}: {
    kind: ApplicationLinkKind
    record: TransportRequestRelationRef | null | undefined
    fallbackTitle?: string
}) => {
    const uuid = uuidOf(record)
    const title =
        typeof record?.title === 'string' && record.title !== '' ? record.title : (fallbackTitle ?? '—')

    if (uuid === null) return <>{title}</>

    return (
        <Typography.Link
            onClick={(event) => {
                event.stopPropagation()
                OpenResourceCardTask({
                    resource: LINKED_RESOURCES[kind],
                    mode: 'view',
                    displayMode: 'drawer',
                    primaryFieldValue: uuid,
                })
            }}
            onDoubleClick={(event) => event.stopPropagation()}
        >
            {title}
        </Typography.Link>
    )
}

/**
 * Поле картки в перегляді — та сама розмітка, що в Ship-шаблонів поля: підпис
 * там само, де в сусідніх полів, і e2e знаходить поле за підписом.
 */
export const ViewCell = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="form-field-view">
        <div className="field-view-label">{label}</div>
        <div className="field-view-value">{children}</div>
    </div>
)

/** Поле лише для читання й у формі: підпис як у ядрового поля, значення під ним. */
const ReadOnlyCell = ({ formContext, label, children }: { formContext: ApplicationFormContextLike; label: string; children: ReactNode }) =>
    formContext.isEditable !== true ? (
        <ViewCell label={label}>{children}</ViewCell>
    ) : (
        <div className="form-field form-field-default">
            <label className="field-label">
                <h2 className="field-label-title">{label}</h2>
            </label>
            <div>{children}</div>
        </div>
    )

/** «Статус» у блоці «Загальне» — лише перегляд: його ставлять резолвер і переходи, не форма. */
export const ApplicationStatusCell = observer(({ formContext }: { formContext: ApplicationFormContextLike }) => {
    const values = formContext.values ?? {}

    return (
        <ReadOnlyCell formContext={formContext} label={L.status}>
            <ApplicationStatus status={values.status} countOffers={values['countOffers']} />
        </ReadOnlyCell>
    )
})

/** Посилання в перегляді: клієнт і перевізник; у формі їх малює ядровий select. */
export const ApplicationLinkViewCell = observer(
    ({ formContext, name, kind }: { formContext: ApplicationFormContextLike; name: string; kind: ApplicationLinkKind }) => {
        const record = formContext.values?.[name] as TransportRequestRelationRef | null | undefined
        const label = (L as Record<string, string>)[name] ?? name

        return (
            <ViewCell label={label}>
                <ApplicationLink kind={kind} record={record} />
            </ViewCell>
        )
    }
)

/**
 * «Запит» і «Тендер» — лише перегляд і в формі: ставить конвертація при
 * створенні, далі не змінюються (ДЗ-04). Посилання відкриває джерело.
 */
export const ApplicationSourceCell = observer(
    ({ formContext, name }: { formContext: ApplicationFormContextLike; name: 'request' | 'tender' }) => {
        const record = formContext.values?.[name] as TransportRequestRelationRef | null | undefined

        return (
            <ReadOnlyCell formContext={formContext} label={L[name]}>
                {record ? <ApplicationLink kind={name} record={record} fallbackTitle={L[name]} /> : '—'}
            </ReadOnlyCell>
        )
    }
)

/** «Прибуток» — лише перегляд, у гривні; `null` — ще не пораховано (ДЗ-06). */
export const ApplicationProfitCell = observer(({ formContext }: { formContext: ApplicationFormContextLike }) => {
    const value = formContext.values?.['amountProfitUAH']
    const amount = typeof value === 'number' ? value : typeof value === 'string' && value !== '' ? Number(value) : null

    return (
        <ReadOnlyCell formContext={formContext} label={L.amountProfitUAH}>
            {amount === null || !Number.isFinite(amount) ? '—' : formatMoneyWithCurrency(amount, 'UAH')}
        </ReadOnlyCell>
    )
})

/**
 * Шапка картки: «Заявка: <клієнт>» (легасі `ApplicationResource.jsx:14-25`),
 * статус бейджем і «Ід». Окремий observer-компонент: читає значення, які
 * редагують, а ядровий `FormField` на кожне перемальовування шаблону
 * перемонтовував би поля.
 */
export const ApplicationCardHeader = observer(({ formContext }: { formContext: ApplicationFormContextLike }) => {
    const values = formContext.values ?? {}
    const status = values.status
    const client = typeof values.client?.title === 'string' && values.client.title !== '' ? values.client.title : null

    return (
        <EntityHeader
            {...(typeof values.id === 'number' ? { code: `Ід ${values.id}` } : {})}
            title={client ? `Заявка: ${client}` : 'Заявка'}
            status={
                status && typeof status.title === 'string'
                    ? { label: status.title, ...(status.color ? { color: status.color } : {}) }
                    : undefined
            }
        />
    )
})
